import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import {
  Subscriber,
  SubscriberMetadata,
  UpsertSubscriberRequest,
  createSubscriber,
  deleteSubscriber,
  getSubscriberMetadata,
  getSubscribers,
  updateSubscriber,
} from "../../../services/SubscribersService";

interface SubscribersPageProps {
  onBack?: () => void;
  initialData?: Subscriber[] | null;
  onDataLoaded?: (data: Subscriber[]) => void;
}

type SubscriberFormState = UpsertSubscriberRequest;

const PAGE_SIZE = 20;

const emptyForm: SubscriberFormState = {
  name: "",
  email: "",
  invoiceReference: "",
  organization1: "",
  organization2: "",
  substituteSubscriberId: null,
  isApprover: false,
  userId: null,
  isActive: true,
};

export default function SubscribersPage({
  onBack: _onBack,
  initialData,
  onDataLoaded,
}: Readonly<SubscribersPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 513,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const [data, setData] = useState<Subscriber[]>(initialData ?? []);
  const [loading, setLoading] = useState(!initialData);
  const [modalOpened, setModalOpened] = useState(false);
  const [editingSubscriberId, setEditingSubscriberId] = useState<number | null>(
    null,
  );
  const [form, setForm] = useState<SubscriberFormState>(emptyForm);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<SubscriberMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [accessDenied, setAccessDenied] = useState(false);

  const canManage = checkAccess("ADMIN_PRICES");

  const isForbiddenError = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status === 403;

  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  const showError = useCallback(
    (fallback: string, error: unknown) => {
      console.error(error);
      const validationErrors = (error as {
        response?: { data?: { errors?: Record<string, string[]>; message?: string } };
      })?.response?.data?.errors;
      const validationMessage = validationErrors
        ? Object.values(validationErrors).flat()[0]
        : null;
      const message =
        validationMessage ||
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error instanceof Error ? error.message : fallback);
      notifications.show({
        title: t("REPORTS_ERROR", "Error"),
        message,
        color: "red",
      });
    },
    [t],
  );

  const getDuplicateNameValidationMessage = useCallback(
    (name: string) => {
      const normalizedName = name.trim().toLowerCase();
      if (!normalizedName || editingSubscriberId) {
        return null;
      }

      const duplicateExists = data.some(
        (subscriber) => subscriber.name.trim().toLowerCase() === normalizedName,
      );

      return duplicateExists
        ? t("SUBSCRIBERS_DUPLICATE_NAME", "Subscriber name already exists")
        : null;
    },
    [data, editingSubscriberId, t],
  );

  const loadSubscribers = useCallback(async () => {
    if (selectedAddressSystemId <= 0) {
      setData([]);
      onDataLoaded?.([]);
      setLoading(false);
      return;
    }

    if (!canManage) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setAccessDenied(false);
      const result = await getSubscribers(selectedAddressSystemId);
      setData(result);
      onDataLoaded?.(result);
    } catch (error) {
      if (isForbiddenError(error)) {
        setAccessDenied(true);
        setData([]);
        onDataLoaded?.([]);
      } else {
        showError("Failed to load subscribers", error);
      }
    } finally {
      setLoading(false);
    }
  }, [canManage, onDataLoaded, selectedAddressSystemId, showError]);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setLoading(false);
      return;
    }

    void loadSubscribers();
  }, [initialData, loadSubscribers]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
    if (activePage > totalPages) {
      setActivePage(totalPages);
    }
  }, [activePage, data.length]);

  const loadMetadata = useCallback(async () => {
    setMetadataLoading(true);
    try {
      setAccessDenied(false);
      const result = await getSubscriberMetadata(selectedAddressSystemId);
      setMetadata(result);
      return result;
    } catch (error) {
      if (isForbiddenError(error)) {
        setAccessDenied(true);
        setMetadata(null);
        return null;
      }

      throw error;
    } finally {
      setMetadataLoading(false);
    }
  }, [selectedAddressSystemId]);

  const closeModal = (force = false) => {
    if (saving && !force) {
      return;
    }

    setModalOpened(false);
    setEditingSubscriberId(null);
    setForm(emptyForm);
    setNameError(null);
    setEmailError(null);
  };

  const getEmailValidationMessage = useCallback(
    (email: string | null | undefined) => {
      const normalizedEmail = email?.trim();
      if (!normalizedEmail) {
        return null;
      }

      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
      return isValidEmail
        ? null
        : t("SUBSCRIBERS_INVALID_EMAIL", "Enter a valid email address");
    },
    [t],
  );

  const openCreate = () => {
    setEditingSubscriberId(null);
    setForm(emptyForm);
    setModalOpened(true);
    void loadMetadata().catch((error) =>
      showError("Failed to load subscriber form metadata", error),
    );
  };

  const openEdit = (subscriber: Subscriber) => {
    setEditingSubscriberId(subscriber.id);
    setForm({
      name: subscriber.name,
      email: subscriber.email ?? "",
      invoiceReference: subscriber.invoiceReference ?? "",
      organization1: subscriber.organization1 ?? "",
      organization2: subscriber.organization2 ?? "",
      substituteSubscriberId: subscriber.substituteSubscriberId ?? null,
      isApprover: subscriber.isApprover,
      userId: subscriber.userId ?? null,
      isActive: subscriber.isActive,
    });
    setModalOpened(true);
    void loadMetadata().catch((error) =>
      showError("Failed to load subscriber form metadata", error),
    );
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      setNameError(null);
      return t("SUBSCRIBERS_NAME_REQUIRED", "Name is required");
    }

    const currentNameError = getDuplicateNameValidationMessage(form.name);
    setNameError(currentNameError);
    if (currentNameError) {
      return currentNameError;
    }

    const currentEmailError = getEmailValidationMessage(form.email);
    setEmailError(currentEmailError);
    if (currentEmailError) {
      return currentEmailError;
    }

    return null;
  };

  const buildRequest = (): UpsertSubscriberRequest => ({
    name: form.name.trim(),
    email: form.email?.trim() || null,
    invoiceReference: form.invoiceReference?.trim() || null,
    organization1: form.organization1?.trim() || null,
    organization2: form.organization2?.trim() || null,
    substituteSubscriberId: form.substituteSubscriberId ?? null,
    isApprover: form.isApprover,
    userId: form.userId ?? null,
    isActive: form.isActive,
  });

  const buildSubscriberFromRequest = (
    id: number,
    request: UpsertSubscriberRequest,
    existingSubscriber?: Subscriber,
  ): Subscriber => {
    const substituteSubscriber = metadata?.subscriberOptions.find(
      (option) => option.id === request.substituteSubscriberId,
    );
    const linkedUser = metadata?.userOptions.find(
      (option) => option.id === request.userId,
    );

    return {
      id,
      name: request.name,
      email: request.email ?? null,
      addressSystemId: selectedAddressSystemId,
      invoiceReference: request.invoiceReference ?? null,
      organization1: request.organization1 ?? null,
      organization2: request.organization2 ?? null,
      substituteSubscriberId: request.substituteSubscriberId ?? null,
      substituteSubscriberName: substituteSubscriber?.name ?? null,
      isApprover: request.isApprover,
      userId: request.userId ?? null,
      userName: linkedUser?.name ?? null,
      isActive: request.isActive,
      isInUse: existingSubscriber?.isInUse ?? false,
    };
  };

  const sortSubscribers = (subscribers: Subscriber[]) =>
    [...subscribers].sort((left, right) => left.name.localeCompare(right.name));

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      notifications.show({
        title: t("REPORTS_ERROR", "Error"),
        message: validationError,
        color: "red",
      });
      return;
    }

    setSaving(true);
    try {
      const request = buildRequest();
      const currentEditingSubscriberId = editingSubscriberId;
      let savedSubscriberId: number;

      if (currentEditingSubscriberId) {
        await updateSubscriber(
          selectedAddressSystemId,
          currentEditingSubscriberId,
          request,
        );
        savedSubscriberId = currentEditingSubscriberId;
      } else {
        savedSubscriberId = await createSubscriber(selectedAddressSystemId, request);
      }

      setData((current) => {
        const existingSubscriber = current.find(
          (subscriber) => subscriber.id === savedSubscriberId,
        );
        const nextSubscriber = buildSubscriberFromRequest(
          savedSubscriberId,
          request,
          existingSubscriber,
        );
        const nextData = currentEditingSubscriberId
          ? current.map((subscriber) =>
              subscriber.id === savedSubscriberId ? nextSubscriber : subscriber,
            )
          : [...current, nextSubscriber];
        const sortedData = sortSubscribers(nextData);
        onDataLoaded?.(sortedData);
        return sortedData;
      });

      closeModal(true);
      notifications.show({
        title: t("SUBSCRIBERS_SUCCESS", "Success"),
        message: currentEditingSubscriberId
          ? t("SUBSCRIBERS_UPDATED", "Subscriber updated")
          : t("SUBSCRIBERS_CREATED", "Subscriber created"),
        color: "green",
      });
    } catch (error) {
      showError("Failed to save subscriber", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (subscriber: Subscriber) => {
    try {
      await deleteSubscriber(selectedAddressSystemId, subscriber.id);
      setData((current) => {
        const nextData = current.filter((item) => item.id !== subscriber.id);
        onDataLoaded?.(nextData);
        return nextData;
      });
      notifications.show({
        title: t("SUBSCRIBERS_SUCCESS", "Success"),
        message: t("SUBSCRIBERS_DELETED", "Subscriber deleted"),
        color: "green",
      });
    } catch (error) {
      showError("Failed to delete subscriber", error);
    }
  };

  const confirmDelete = (subscriber: Subscriber) => {
    modals.openConfirmModal({
      title: t("SUBSCRIBERS_DELETE_TITLE", "Delete subscriber"),
      children: (
        <Text size="sm">
          {t(
            "SUBSCRIBERS_DELETE_CONFIRMATION",
            `Delete subscriber \"${subscriber.name}\"?`,
          )}
        </Text>
      ),
      labels: {
        confirm: t("COMMON_DELETE", "Delete"),
        cancel: t("COMMON_CANCEL", "Cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: () => {
        void handleDelete(subscriber);
      },
    });
  };

  const userOptions = useMemo(
    () =>
      (metadata?.userOptions ?? []).map((option) => ({
        value: option.id.toString(),
        label: option.email ? `${option.name} (${option.email})` : option.name,
      })),
    [metadata],
  );

  const substituteOptions = useMemo(
    () =>
      (metadata?.subscriberOptions ?? [])
        .filter((option) => option.id !== editingSubscriberId)
        .map((option) => ({
          value: option.id.toString(),
          label: option.email ? `${option.name} (${option.email})` : option.name,
        })),
    [editingSubscriberId, metadata],
  );

  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const pagedSubscribers = useMemo(
    () => data.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE),
    [activePage, data],
  );
  const startIndex = data.length === 0 ? 0 : (activePage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(activePage * PAGE_SIZE, data.length);

  return (
    <Stack h="100%" gap="md">
      <Group justify="space-between">
        <Text fw={700}>{t("TEXT_LINK_SUBSCRIBERS", "Subscribers")}</Text>
        {canManage && !accessDenied && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            {t("SUBSCRIBERS_ADD", "Add subscriber")}
          </Button>
        )}
      </Group>

      {loading ? (
        <Group justify="center" style={{ flex: 1 }}>
          <Loader size="sm" />
        </Group>
      ) : accessDenied ? (
        <Text c="dimmed" size="sm">
          {t(
            "SETTINGS_NO_LINKS_FOR_ROLE",
            "No settings links match your permissions.",
          )}
        </Text>
      ) : data.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("SUBSCRIBERS_EMPTY", "No subscribers found")}
        </Text>
      ) : (
        <>
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              border: "1px solid var(--mantine-color-gray-3)",
              borderRadius: "var(--mantine-radius-sm)",
            }}
          >
            <Table striped highlightOnHover withTableBorder style={{ minWidth: 960 }}>
              <Table.Thead>
                <Table.Tr>
                   <Table.Th>{t("TEXT_TBL_NAME_SUBSCRIBE", "Name")}</Table.Th>
                   <Table.Th>{t("TEXT_TBL_EMAIL_SUBSCRIBE", "Email")}</Table.Th>
                  <Table.Th>{t("TEXT_TBL_ORGANIZATION_SUBSCRIBE", "Organization")}</Table.Th>
                  <Table.Th>{t("TEXT_TBL_LINKED_USER_SUBSCRIBE", "Linked user")}</Table.Th>
                  <Table.Th>{t("TEXT_TBL_SUBSTITUTE_SUBSCRIBE", "Substitute")}</Table.Th>
                  <Table.Th>{t("TEXT_TBL_APPROVER_SUBSCRIBE", "Approver")}</Table.Th>
                  <Table.Th>{t("TEXT_TBL_ACTIVE_SUBSCRIBE", "Active")}</Table.Th>
                  {canManage && (
                    <Table.Th style={{ width: 112 }}>
                      {t("TEXT_TBL_ACTIONS_SUBSCRIBE", "Actions")}
                    </Table.Th>
                  )}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedSubscribers.map((subscriber) => (
                  <Table.Tr key={subscriber.id}>
                    <Table.Td>{subscriber.name}</Table.Td>
                    <Table.Td>{subscriber.email || "-"}</Table.Td>
                    <Table.Td>
                      {[subscriber.organization1, subscriber.organization2]
                        .filter(Boolean)
                        .join(" / ") || "-"}
                    </Table.Td>
                    <Table.Td>{subscriber.userName || "-"}</Table.Td>
                    <Table.Td>{subscriber.substituteSubscriberName || "-"}</Table.Td>
                    <Table.Td>
                      {subscriber.isApprover
                        ? t("COMMON_YES", "Yes")
                        : t("COMMON_NO", "No")}
                    </Table.Td>
                    <Table.Td>
                      {subscriber.isActive
                        ? t("COMMON_YES", "Yes")
                        : t("COMMON_NO", "No")}
                    </Table.Td>
                    {canManage && (
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            aria-label={t("COMMON_EDIT", "Edit")}
                            onClick={() => openEdit(subscriber)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t("COMMON_DELETE", "Delete")}
                            disabled={subscriber.isInUse}
                            title={
                              subscriber.isInUse
                                ? t(
                                    "SUBSCRIBERS_DELETE_DISABLED_IN_USE",
                                    "Subscriber cannot be deleted because it is in use",
                                  )
                                : undefined
                            }
                            onClick={() => confirmDelete(subscriber)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
          <Group justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              {`${t("SUBSCRIBERS_SHOWING", "Showing")} ${startIndex}-${endIndex} ${t("SUBSCRIBERS_OF", "of")} ${data.length}`}
            </Text>
            {totalPages > 1 && (
              <Pagination
                value={activePage}
                onChange={setActivePage}
                total={totalPages}
                size="sm"
                siblings={1}
                boundaries={1}
              />
            )}
          </Group>
        </>
      )}

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={
          editingSubscriberId
            ? t("SUBSCRIBERS_EDIT_TITLE", "Edit subscriber")
            : t("SUBSCRIBERS_CREATE_TITLE", "Add subscriber")
        }
        size="lg"
      >
        <Stack>
          <TextInput
            label={t("SUBSCRIBERS_NAME", "Name")}
            value={form.name}
            error={nameError}
            onChange={(event) => {
              const nextName = event.currentTarget.value;
              setForm((current) => ({ ...current, name: nextName }));
              setNameError(getDuplicateNameValidationMessage(nextName));
            }}
            required
          />
          <TextInput
            label={t("SUBSCRIBERS_EMAIL", "Email")}
            value={form.email ?? ""}
            type="email"
            error={emailError}
            onChange={(event) => {
              const nextEmail = event.currentTarget.value;
              setForm((current) => ({ ...current, email: nextEmail }));
              setEmailError(getEmailValidationMessage(nextEmail));
            }}
          />
          <TextInput
            label={t("SUBSCRIBERS_INVOICE_REFERENCE", "Invoice reference")}
            value={form.invoiceReference ?? ""}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                invoiceReference: event.currentTarget.value,
              }))
            }
          />
          <Group grow>
            <TextInput
              label={t("SUBSCRIBERS_ORGANIZATION_1", "Organization 1")}
              value={form.organization1 ?? ""}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  organization1: event.currentTarget.value,
                }))
              }
            />
            <TextInput
              label={t("SUBSCRIBERS_ORGANIZATION_2", "Organization 2")}
              value={form.organization2 ?? ""}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  organization2: event.currentTarget.value,
                }))
              }
            />
          </Group>
          <Group grow>
            <Select
              label={t("SUBSCRIBERS_LINKED_USER", "Linked user")}
              placeholder={t("SUBSCRIBERS_LINKED_USER_PLACEHOLDER", "Select user")}
              searchable
              clearable
              disabled={metadataLoading}
              data={userOptions}
              value={form.userId?.toString() ?? null}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  userId: value ? Number(value) : null,
                }))
              }
            />
            <Select
              label={t("SUBSCRIBERS_SUBSTITUTE", "Substitute")}
              placeholder={t(
                "SUBSCRIBERS_SUBSTITUTE_PLACEHOLDER",
                "Select substitute subscriber",
              )}
              searchable
              clearable
              disabled={metadataLoading}
              data={substituteOptions}
              value={form.substituteSubscriberId?.toString() ?? null}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  substituteSubscriberId: value ? Number(value) : null,
                }))
              }
            />
          </Group>
          <Group>
            <Checkbox
              label={t("SUBSCRIBERS_APPROVER", "Approver")}
              checked={form.isApprover}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  isApprover: event.currentTarget.checked,
                }))
              }
            />
            <Checkbox
              label={t("SUBSCRIBERS_ACTIVE", "Active")}
              checked={form.isActive}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  isActive: event.currentTarget.checked,
                }))
              }
            />
          </Group>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => closeModal()}
              disabled={saving}
            >
              {t("COMMON_CANCEL", "Cancel")}
            </Button>
            <Button onClick={() => void handleSave()} loading={saving}>
              {t("COMMON_SAVE", "Save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
